import { classNames } from "../utils/classNames";
import { ChangeEvent, ChangeEventHandler, FocusEvent, ReactElement } from "react";
import { Box } from "./Box";
import { observer } from "mobx-react";
import { Alert } from "./Alert";
import { isTMessage, TMessage } from "../i18n/tt";

import { useTranslation } from "../stores/RootStoreContext";
import { RadioFieldModel } from "../common/commonForms";
import { CommonProps } from "./boxHelpers";

export type RadioButtonProps = {
  name?: string;
  label?: ReactElement | string | number | TMessage;
  description?: ReactElement | string | number | TMessage;
  checked?: boolean;
  disabled?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
};

export function RadioButton({
  name,
  label,
  description,
  checked,
  disabled,
  onChange,
}: RadioButtonProps) {
  const { t } = useTranslation();
  const radioButton = (
    <div className={"tw-relative tw-w-5 tw-h-5"}>
      <input
        type="checkbox"
        className={classNames(
          "tw-absolute",
          "tw-appearance-none",
          "tw-w-5",
          "tw-h-5",
          "tw-border-2",
          "tw-rounded-full",
          "bg-default",
          disabled && ["tw-cursor-default", "border-gray"],
          !disabled &&
            checked && [
              "tw-cursor-pointer",
              "border-primary",
              "hover:border-primary-dark",
              "tw-cursor-pointer",
            ],
          !disabled &&
            !checked && ["bg-border-gray-hard", "hover:border-primary", "hover:bg-primary-light"],
        )}
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <div
        className={classNames(
          "tw-absolute",
          "tw-w-3",
          "tw-h-3",
          "tw-inset-1",
          "tw-rounded-full",
          "tw-border",
          "border-primary-contrast",
          "tw-pointer-events-none",
          checked ? "tw-block" : "tw-hidden",
          disabled ? ["bg-gray"] : ["bg-primary", "hover:bg-primary-dark"],
        )}
      ></div>
    </div>
  );

  return label ? (
    <label
      className={classNames(
        "tw-flex",
        "tw-gap-2",
        "tw-items-start",
        disabled ? ["tw-cursor-default", "subtle"] : ["tw-cursor-pointer", "color-default"],
      )}
    >
      <div
        style={{
          paddingTop: 2 /* yuck! need to figure out a better solution to this (this happens when the label wraps) */,
        }}
      >
        {radioButton}
      </div>
      {description ? (
        <div>
          <div>{isTMessage(label) ? t(label) : label}</div>
          <div className={"text-sm subtle"}>
            {isTMessage(description) ? t(description) : description}
          </div>
        </div>
      ) : (
        <div>{isTMessage(label) ? t(label) : label}</div>
      )}
    </label>
  ) : (
    radioButton
  );
}

export type RadioButtonGroupProps<T> = {
  disabled?: boolean;
  horizontal?: boolean;
  idFn: (item: T) => string;
  labelFn: (item: T) => string | ReactElement | string | number | TMessage;
  descriptionFn?: (item: T) => (ReactElement | string | number) | undefined | TMessage;
  disabledFn?: (item: T) => boolean;
  name?: string;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>, selected: T) => void;
  options: T[];
  selected?: T;
  error?: TMessage;
  gap?: CommonProps["gap"];
};

export const RadioButtonGroup = observer(function RadioButtonGroup<T>({
  name,
  horizontal,
  options,
  selected,
  idFn,
  labelFn,
  descriptionFn,
  disabledFn,
  disabled,
  onChange,
  error,
}: RadioButtonGroupProps<T>) {
  const { t } = useTranslation();
  return (
    <>
      <Box vertical={!horizontal} gap={horizontal ? "md" : "sm"}>
        {options.map((option) => (
          <RadioButton
            key={idFn(option)}
            name={name}
            disabled={disabled || disabledFn?.(option)}
            label={labelFn(option)}
            description={descriptionFn?.(option)}
            onChange={(event) => {
              if (event.target.checked) {
                onChange?.(event, option);
              }
            }}
            checked={selected !== undefined && idFn(selected) === idFn(option)}
          />
        ))}
      </Box>
      {error !== undefined && <Alert type={"error"} message={<>{t(error)}</>} />}
    </>
  );
});

export const MRadioButtonGroup = observer(function MRadioButtonGroup<T>({
  model,
  name,
  options,
  idFn,
  labelFn,
  descriptionFn,
  disabledFn,
  disabled,
  horizontal,
  onChange,
  gap,
}: {
  model: RadioFieldModel<T>;
} & Partial<RadioButtonGroupProps<T>>) {
  const { t } = useTranslation();
  return (
    <>
      <Box vertical={!horizontal} gap={horizontal ? (gap ?? "md") : (gap ?? "sm")}>
        {(options ?? model.options).map((option) => (
          <RadioButton
            key={(idFn ?? model.getId)(option)}
            name={name}
            disabled={disabled || disabledFn?.(option) || model.isDisabled(option)}
            label={labelFn?.(option) ?? model.getLabel(option)}
            description={descriptionFn ? descriptionFn(option) : model.getDescription(option)}
            onChange={(event) => {
              if (event.target.checked) {
                if (onChange) {
                  onChange(event, option);
                } else {
                  model.setValue(option);
                }
              }
            }}
            checked={model.getId(model.value) === model.getId(option)}
          />
        ))}
      </Box>
      {model.error !== undefined && <Alert type={"error"} message={<>{t(model.error)}</>} />}
    </>
  );
});
